import get from 'lodash/get';

import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';

import type { SkuModuleMeta } from '../pipeline-kernel/types';

/** 模块是否为「下单列表项级锁」（与算价无关） */
export function isOrderListItemLockModule(moduleKey: string, skuModuleMeta: Record<string, SkuModuleMeta>): boolean {
  return skuModuleMeta[moduleKey]?.orderListItemLock === true;
}

/** 读取 SKU preset 中的数组（兼容数组 / JSON 字符串） */
function readPresetArrayAtPath(preset: Record<string, unknown> | undefined | null, path: string): unknown[] {
  if (!preset || !path) return [];
  const raw = get(preset, path);
  const parsed = Array.isArray(raw) ? raw : parseFormJsonField<unknown>(raw);
  return Array.isArray(parsed) ? parsed : [];
}

/** 从预置数组提取稳定 key 集合（支持对象行或纯字符串项） */
export function buildPresetKeySet(
  preset: Record<string, unknown> | undefined | null,
  path: string,
  resolveKey: (item: unknown) => string,
): Set<string> {
  const keys = new Set<string>();
  for (const item of readPresetArrayAtPath(preset, path)) {
    const key = resolveKey(item).trim();
    if (key) keys.add(key);
  }
  return keys;
}

/** 读取 SKU preset 中的对象列表（兼容数组 / JSON 字符串） */
export function readPresetListAtPath(preset: Record<string, unknown> | undefined | null, path: string): Record<string, unknown>[] {
  return readPresetArrayAtPath(preset, path).filter(
    (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
  );
}

/** 从预置对象列表提取稳定 key 集合（如环境变量 name、服务 name） */
export function buildPresetListKeySet(
  preset: Record<string, unknown> | undefined | null,
  path: string,
  resolveKey: (row: Record<string, unknown>) => string,
): Set<string> {
  return buildPresetKeySet(preset, path, (item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return '';
    return resolveKey(item as Record<string, unknown>);
  });
}

export function resolveNamedListItemKey(row: Record<string, unknown> | undefined | null, nameField = 'name'): string {
  if (!row) return '';
  return String(row[nameField] ?? '').trim();
}

/** DNS nameserver / search：兼容 `{ server|search }` 与纯字符串 */
export function resolveDnsListItemKey(item: unknown, field: 'server' | 'search'): string {
  if (typeof item === 'string') return item.trim();
  if (item && typeof item === 'object' && !Array.isArray(item)) {
    return resolveNamedListItemKey(item as Record<string, unknown>, field);
  }
  return '';
}

/** 读取 SKU preset 中的字符串列表（如 IP / CIDR 白名单） */
export function readPresetStringListAtPath(preset: Record<string, unknown> | undefined | null, path: string): string[] {
  return readPresetArrayAtPath(preset, path)
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
}

/** 字符串列表预置 key 集合 */
export function buildPresetStringListKeySet(preset: Record<string, unknown> | undefined | null, path: string): Set<string> {
  return buildPresetKeySet(preset, path, (item) => String(item ?? ''));
}
